/**
 * The compliance graph (ADR-029 §4): which descriptors are shared spines and which are
 * framework registers, the fact-type names rule tables use, and the typed edges that link
 * spines. Edges live in the kernel graph (GraphConcept, `graph_edge`), so a link made once
 * — "this supplier accesses health data" — is read by every framework.
 *
 * Runtime-free on purpose: the rule linter, the wizard seed and pack-decisions all read it.
 *
 * System of record (decided 2026-10-14, governance backlog C14): the seven new spines are
 * kernel-native — the kernel is their SoR, written by the journey engine. Edges are always
 * kernel-native. coreBOS stays SoR for the harvested modules; scripts/corebos/project_to_kernel.py
 * mirrors them (and their reference fields, as edges) into the kernel read model.
 *
 * `base: true` (ADR-030 §3, ADR-031): the descriptor is authored once in `@evo-flywheel/contracts/base`
 * because two kits consume it (ai_system: ai-portal registry + compliance AI Act tables). Its coreBOS
 * module (evSistemaAI) is installed from contracts too, so on a customer's one coreBOS both kits
 * read and write the same record: one customer, one coreBOS, one record.
 */
/** Fact-type name used in rule tables and journeys -> graph node. */
export const factTypes = {
    // CRM base (imported, never authored by the kit)
    person: { slug: 'contact', role: 'spine', backing: 'crm' },
    // kernel-native spines (src/descriptors/spines/)
    organisation: { slug: 'cmp_organisation_profile', role: 'spine', backing: 'kernel' },
    org_unit: { slug: 'cmp_org_unit', role: 'spine', backing: 'kernel' },
    role: { slug: 'cmp_role', role: 'spine', backing: 'kernel' },
    activity: { slug: 'cmp_activity', role: 'spine', backing: 'kernel' },
    location: { slug: 'cmp_location', role: 'spine', backing: 'kernel' },
    data_category: { slug: 'cmp_data_category', role: 'spine', backing: 'kernel' },
    data_subject_category: { slug: 'cmp_data_subject_category', role: 'spine', backing: 'kernel' },
    // coreBOS-backed spines (harvested)
    asset: { slug: 'cmp_asset', role: 'spine', backing: 'corebos' },
    third_party: { slug: 'cmp_third_party', role: 'spine', backing: 'corebos' },
    risk: { slug: 'cmp_threat', role: 'spine', backing: 'corebos' },
    control: { slug: 'cmp_measure', role: 'spine', backing: 'corebos' },
    policy: { slug: 'cmp_policy', role: 'spine', backing: 'corebos' },
    content: { slug: 'cmp_content', role: 'spine', backing: 'corebos' },
    attestation: { slug: 'cmp_acceptance', role: 'spine', backing: 'corebos' },
    incident: { slug: 'cmp_data_breach', role: 'spine', backing: 'corebos' },
    assessment: { slug: 'cmp_audit', role: 'spine', backing: 'corebos' },
    // contracts base (shared with the ai-portal kit)
    ai_system: { slug: 'ai_system', role: 'spine', backing: 'corebos', base: true },
    // framework registers: views over spines
    processing_activity: { slug: 'cmp_processing_activity', role: 'register', backing: 'corebos', frameworks: ['gdpr'] },
    data_flow: { slug: 'cmp_data_flow', role: 'register', backing: 'corebos', frameworks: ['gdpr'] },
    worker_group: { slug: 'cmp_worker_group', role: 'register', backing: 'corebos', frameworks: ['dlgs81'] },
    aml_verification: { slug: 'cmp_aml_verification', role: 'register', backing: 'corebos', frameworks: ['aml'] },
};
/**
 * Typed links between graph nodes (fact-type names). Stored as kernel graph edges; the kernel
 * is their system of record even when both ends live in coreBOS. Rule tables read them as
 * `<from>.has.<relation>` (at least one edge) and `<from>.any.<relation>.<field>` (some target
 * has a truthy field).
 */
export const edgeTypes = [
    // structure and people
    { relation: 'member_of', from: 'person', to: 'org_unit', label: 'appartiene a' },
    { relation: 'holds_role', from: 'person', to: 'role', label: 'ricopre il ruolo' },
    { relation: 'in_group', from: 'person', to: 'worker_group', label: 'appartiene al gruppo omogeneo' },
    { relation: 'attested', from: 'person', to: 'attestation', label: 'ha sottoscritto' },
    // GDPR register over spines (Art. 30(1))
    { relation: 'part_of', from: 'processing_activity', to: 'activity', label: 'fa parte di' },
    { relation: 'performed_by', from: 'processing_activity', to: 'org_unit', label: 'svolto da' },
    { relation: 'processes', from: 'processing_activity', to: 'data_category', label: 'tratta' },
    { relation: 'concerns', from: 'processing_activity', to: 'data_subject_category', label: 'riguarda' },
    { relation: 'shared_with', from: 'processing_activity', to: 'third_party', label: 'comunicato a' },
    { relation: 'runs_on', from: 'processing_activity', to: 'asset', label: 'su' },
    { relation: 'protected_by', from: 'processing_activity', to: 'control', label: 'protetto da' },
    // assets, suppliers, risk
    { relation: 'holds', from: 'asset', to: 'data_category', label: 'contiene' },
    { relation: 'located_in', from: 'asset', to: 'location', label: 'si trova in' },
    { relation: 'protected_by', from: 'asset', to: 'control', label: 'protetto da' },
    { relation: 'accesses', from: 'third_party', to: 'data_category', label: 'accede a' },
    { relation: 'supports', from: 'third_party', to: 'activity', label: 'supporta' },
    { relation: 'documented_by', from: 'third_party', to: 'policy', label: 'documentato da' },
    { relation: 'affects', from: 'risk', to: 'asset', label: 'minaccia' },
    { relation: 'mitigates', from: 'control', to: 'risk', label: 'mitiga' },
    { relation: 'affects', from: 'incident', to: 'asset', label: 'ha colpito' },
    { relation: 'involves', from: 'incident', to: 'data_category', label: 'coinvolge' },
    // 81/08 register over spines
    { relation: 'performs', from: 'worker_group', to: 'role', label: 'mansione' },
    { relation: 'works_at', from: 'worker_group', to: 'location', label: 'luogo di lavoro' },
    { relation: 'exposed_to', from: 'worker_group', to: 'risk', label: 'esposto a' },
    // policies and attestations
    { relation: 'applies_to', from: 'policy', to: 'org_unit', label: 'si applica a' },
    { relation: 'attests', from: 'attestation', to: 'policy', label: 'accetta' },
    // AI systems (AI Act overlay, governance docs/regulations/aiact/crosswalk.yaml)
    { relation: 'processes', from: 'ai_system', to: 'data_category', label: 'tratta' },
    { relation: 'used_in', from: 'ai_system', to: 'activity', label: 'usato in' },
    { relation: 'runs_on', from: 'ai_system', to: 'asset', label: 'su' },
    { relation: 'documented_by', from: 'ai_system', to: 'policy', label: 'documentato da' },
    { relation: 'assessed_by', from: 'ai_system', to: 'assessment', label: 'valutato da' },
    { relation: 'supplies', from: 'third_party', to: 'ai_system', label: 'fornisce' },
    { relation: 'involves_ai', from: 'incident', to: 'ai_system', label: 'coinvolge il sistema AI' },
    // data flows (from coreBOS references dflinktoprocact / dflinktothird)
    { relation: 'traces', from: 'data_flow', to: 'processing_activity', label: 'traccia' },
    { relation: 'flows_to', from: 'data_flow', to: 'third_party', label: 'verso' },
];
